import type { ReactElement } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { RunningNowListItem } from '@kirin/api-client/contracts/shadow-ai-running-now';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { Badge } from '@/components/ui/badge';
import { withTruncation } from '@/components/shared/hocs/withTruncation';
import type { TruncationInjectedProps } from '@/components/shared/hocs/withTruncation';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CategoryBadge } from './CategoryBadge';
import { ProcessNameWithStatus } from './ProcessNameWithStatus';

interface ToolNameCellInnerProps extends TruncationInjectedProps {
  row: RunningNowListItem;
}

const ToolNameCellInner = withTruncation<ToolNameCellInnerProps>(
  ({ row, truncationRef, isTruncated, checkTruncation }) => {
    const nameSpan = (
      <span
        ref={truncationRef}
        className="font-medium truncate min-w-0"
        onMouseEnter={checkTruncation}
        onFocus={checkTruncation}
      >
        {row.toolName}
      </span>
    );

    return (
      <ProcessNameWithStatus className="flex items-center gap-2 min-w-0">
        {isTruncated ? (
          <Tooltip>
            <TooltipTrigger asChild>{nameSpan}</TooltipTrigger>
            <TooltipContent side="top" className="max-w-sm">
              {row.toolName}
            </TooltipContent>
          </Tooltip>
        ) : (
          nameSpan
        )}
        {row.isRoot && (
          <Badge
            variant="destructive"
            className="px-1.5 py-0 gap-1 [&>svg]:size-3"
          >
            <AlertTriangle />
            root
          </Badge>
        )}
      </ProcessNameWithStatus>
    );
  }
);

export function ToolNameCell({
  row,
}: {
  row: RunningNowListItem;
}): ReactElement {
  return <ToolNameCellInner row={row} />;
}

export function CategoryCell({
  row,
}: {
  row: { category: string };
}): ReactElement {
  return <CategoryBadge category={row.category} />;
}

export function PidCell({ row }: { row: RunningNowListItem }): ReactElement {
  return (
    <span className="text-muted-foreground font-mono text-xs">{row.pid}</span>
  );
}

export function PortCell({ row }: { row: RunningNowListItem }): ReactElement {
  if (row.port === null) {
    return <span className="text-muted-foreground font-mono text-xs">—</span>;
  }
  return (
    <span className="text-muted-foreground font-mono text-xs">{row.port}</span>
  );
}

export function StartedAtCell({
  row,
}: {
  row: RunningNowListItem;
}): ReactElement {
  // "Running For" is a length of time, not a moment, so it always renders as
  // a duration and ignores the per-user absolute/relative Timestamp Format.
  // The absolute UTC start time stays available in the tooltip.
  return (
    <RelativeTimeWithTooltip
      date={row.startedAt}
      format="relative"
      variant="duration"
    />
  );
}
